import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { ColumnSorterInfo, ConfigScope, ConfigSettingType, DefaultPage, FieldType, FilterEvent, FilterName, FormatType, PageEvent, SearchEvent, SortEvent, TableConfig } from '../../../models/custom-table/custom-table.model';
import { Observable, BehaviorSubject, Subscription, EMPTY, forkJoin } from 'rxjs';
import { SiteProductDTO } from 'src/app/models/Products/productDTO';
import { AppState, loadedMenusState } from 'src/app/store/reducers';
import { select, Store } from '@ngrx/store';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SitesService } from '../../../shared/services/sites/sites.service';
import { ProductService } from '../../../shared/services/products/products/product.service';
import { TableConfigService } from '../../../shared/services/table-config/table-config.service';
import { MenuDTO, RouteData } from 'src/app/models/Menu/MenuDTO';
import { Title } from '@angular/platform-browser';
import { subMenusIterator } from 'src/app/shared/utils/menuUtils';
import { map, catchError, finalize, tap, switchMap } from 'rxjs/operators';
import { SnackbarType } from 'src/app/models/snackbar/snackbar.model';
import { SnackBarUtils } from 'src/app/shared/utils/snackbarUtils';
import { setSnackbar } from 'src/app/store/actions/messages/message.actions';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component';
import { EditRowDataDialogComponent } from 'src/app/shared/modules/dialogs/edit-row-data-dialog/edit-row-data-dialog.component';
import { FailedProductsDialogComponent } from '../store-site-products/dialog/failed-products-dialog/failed-products-dialog.component';

const USAGE_CODE = "STORE_FUEL_PRODUCTS_SITE"

interface SaveResult {
  successfulProducts: string [];
  failedToRemove: { productCode: string; code: string}[];
}

@Component({
  selector: 'app-store-fuel-products',
  templateUrl: './store-fuel-products.component.html',
  styleUrls: ['./store-fuel-products.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class StoreFuelProductsComponent implements OnInit {
  @Input() siteCode: string;
  @Input() canWrite: string;

  //menu
  menus$: Observable<MenuDTO[]>;
  menuSub: Subscription;
  tableTitle: string;
  headerIcon: string;

  //Subs
  public siteProductsSub: Subscription;

  //Products
  totalProductsStore$ = new BehaviorSubject<number>(0);

  columnInfo$: Observable<ColumnSorterInfo[]>;
  public isLoading$ = new BehaviorSubject<boolean>(false);

  siteFuelProducts: SiteProductDTO[] = [];

  private lastTableEvent: any;

  isFuelFilter = {
    column: 'isFuel',
    fieldType: FieldType.BOOLEAN,
    operator: FilterName.EQUAL,
    value: true
  }

  tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS
    },
    {
      header: "PosMovements.productCode",
      field: "productCode",
      fieldType: FieldType.STRING,
      identifier: true,
    },
    {
      header: "PosMovements.description",
      field: "productDescription",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "SiteManagement.siteProducts.referencePrice",
      field: "unitPriceGrossReference",
      fieldType: FieldType.NUMBER,
      formatType: FormatType.AMOUNT,
    },
    {
      header: "SiteManagement.siteProducts.storePrice",
      field: "unitPriceGross",
      fieldType: FieldType.NUMBER,
      formatType: FormatType.AMOUNT,
    },
    {
      header: "Products.token",
      field: "token",
      fieldType: FieldType.STRING,
      hidden: true,
    },
  ]

  constructor(
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private titleService: Title,
    private translate: TranslateService,
    private sitesService: SitesService,
    private productService: ProductService,
    private tableConfigService: TableConfigService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.menus$ = this.store.pipe(select(loadedMenusState));
    this.loadScreenTitle();
    this.loadProductsSite();
  }

  loadProductsSite(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ): void {
    const { pageIndex, pageSize } = pageEvent || {};

    this.isLoading$.next(true);

    this.siteProductsSub = this.sitesService
      .getSiteStoreProductListPagination(
        this.siteCode,
        {
          skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
          top: pageSize ?? DefaultPage.PAGE_SIZE,
          realSize: true,
        },
        searchEvent,
        sortEvent,
        filterEvent,
        true
      )
      .pipe(
        map((result: any) => {
          const items = result?.result?.items ?? result?.result ?? [];
          const size = result?.result?.size;
          this.totalProductsStore$.next(size);
          return items
        }),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("SiteManagement.siteProducts.loadSiteProductsError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe((result: SiteProductDTO[]) => {
        this.siteFuelProducts = result;
      });
  }

  loadScreenTitle() {
    this.menuSub = this.menus$.subscribe((menus: MenuDTO[]) => {
      if (menus && menus.length) {
        this.route.data.subscribe((routeData: RouteData) => {
          menus.forEach((menuEl: MenuDTO) => {
            const selectedMenu = subMenusIterator(menuEl, routeData);
            if (selectedMenu) {
              this.headerIcon = selectedMenu.icon;
              this.tableTitle = selectedMenu.description;
              this.titleService.setTitle(this.tableTitle);
            }
          });
        });
      }
    })
  }

  onGetTableData(tableEvent: any) {
    this.lastTableEvent = tableEvent;

    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    this.loadProductsSite(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  onSaveTableConfig(tableColumnInfo: ColumnSorterInfo[]) {
    this.isLoading$.next(true);
    this.tableConfigService
      .saveConfigSettings({
        code: USAGE_CODE,
        settingTypeCode: ConfigSettingType.TABLE_CONFIG,
        scope: ConfigScope.USER,
        value: { tableColumnInfo },
      })
      .pipe(
        tap(() =>
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("TableConfig.SaveSuccess"),
                SnackbarType.SUCCESS
              )
            )
          )
        ),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("TableConfig.SaveError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe();
  }


  private getAllSiteFuelProducts(): Observable<SiteProductDTO[]> {
    return this.sitesService
    .getSiteStoreProductListPagination(
        this.siteCode,
        {
          skip: 0,
          top: DefaultPage.EXCEL_PAGE_SIZE,
          realSize: true,
        }, undefined, undefined, undefined, true)
      .pipe(
         map((result: any) => {
          const items = result?.result?.items ?? result?.result ?? [];
          return items
        }),
      );
  }

  //Fetch errors messages to show as a Warning List (FailedProductsDialog)
  private parseUpdateProductList(response: any, payload: SiteProductDTO[]): SaveResult {
    const errors = response?.result?.errors ?? {};

    const successfulProducts: string[] = []
    const failedToRemove: { productCode: string; code: string}[] = [];
    
    payload.forEach((item) => {
      const itemErrors = errors[item.productCode];

      if(itemErrors) {
        const errorMessages = itemErrors.map((e:any) => e.code).join(' ');
        failedToRemove.push({ productCode: item.productCode, code: errorMessages});
      } else {
        successfulProducts.push(item.productCode)
      }
    });

    return { successfulProducts, failedToRemove}
  }

  private saveFuelProductsList(payload: any[], isEdit: boolean) {
    this.isLoading$.next(true);
     const request$ = isEdit 
    ? this.sitesService
      .createSiteStoreProductList({
        siteCode: this.siteCode,
        productList: payload,
      }) 
    :  this.sitesService
      .updateSiteStoreProductList({
        siteCode: this.siteCode,
        productList: payload,
      });
      
      request$
      .pipe(
        map((response) => this.parseUpdateProductList(response, payload)),
        tap(() => {
          if(this.lastTableEvent) {
            const { pageEvent, sortEvent, searchEvent, filterEvent } = this.lastTableEvent;
            this.loadProductsSite(pageEvent, sortEvent, searchEvent, filterEvent);
          } else {
            this.loadProductsSite();
          }
        }),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant(
                  "SiteManagement.siteProducts.saveProductError"
                ),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe((result) => {
        if(result.failedToRemove.length === 0){
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant(
                  "SiteManagement.siteProducts.saveProductSuccess"
                ),
                SnackbarType.SUCCESS
              )
            )
          );
        } else {
          this.openFailedToLinkProductsDialog(result);
        }
      });
  }

  openFailedToLinkProductsDialog(result: SaveResult): void {
    const dialogData = {
      width: '900px',
      panelClass: 'failed-link-products-dialog',
      autoFocus:false,
      data: {
         failedProducts: result.failedToRemove
      }
    }

    this.dialog.open(FailedProductsDialogComponent, dialogData);
  }
}
